import type { LevelBucket } from "../api/types";

/**
 * Client-side level helpers for Discover cards. The backend sets `levelBucket`
 * only when a source exposes level/grade/age text; title-only sources (the
 * sitemap aggregator, Tyrant) leave it undefined. To still show a useful level
 * on every card, we infer a bucket from the title as a fallback, and otherwise
 * present an honest "All levels" chip rather than hiding the field.
 */

export interface LevelDisplay {
  bucket: LevelBucket | "all";
  label: string;
  /** CSS color used to tint the badge. */
  color: string;
  /** true when we inferred it from the title (vs. a source-provided bucket). */
  inferred: boolean;
}

const COLORS: Record<string, string> = {
  hs: "#3b82f6", // blue
  ms: "#8b5cf6", // violet
  youth: "#14b8a6", // teal
  open: "#9aa3b2", // muted (mixed)
  all: "#9aa3b2",
};

const LABELS: Record<string, string> = {
  hs: "High School",
  ms: "Middle School",
  youth: "Youth",
  open: "All levels",
  all: "All levels",
};

/** Lightweight title-based inference (mirrors the server heuristic, trimmed). */
export function inferLevelFromText(...text: (string | undefined)[]): LevelBucket | undefined {
  const hay = text.filter(Boolean).join(" ").toLowerCase();
  if (!hay.trim()) return undefined;

  const hs =
    /\bhigh[-\s]?school\b|\bvarsity\b|\bjunior varsity\b|\bjv\b|\bscholastic\b|\bhs\b/.test(hay);
  const ms =
    /\bmiddle[-\s]?school\b|\bjunior high\b|\bjr\.?\s?high\b|\bms\b/.test(hay);
  const youth =
    /\byouth\b|\belementary\b|\bbantam\b|\bmidget\b|\bpee[-\s]?wee\b|\brookie\b|\bnovice\b|\bkids?\b|\bk-?[568]\b|\b\d{1,2}\s*u\b|\bu\s*\d{1,2}\b|\b\d{1,2}\s*(?:&|and)\s*under\b/.test(
      hay,
    );

  const hits = [hs, ms, youth].filter(Boolean).length;
  if (hits > 1) return "open";
  if (hs) return "hs";
  if (ms) return "ms";
  if (youth) return "youth";
  return undefined;
}

/**
 * Resolve the level to display for a card: prefer the server bucket, fall back
 * to title inference, else "all". Always returns a displayable value.
 */
export function levelDisplay(
  bucket: LevelBucket | undefined,
  ...inferText: (string | undefined)[]
): LevelDisplay {
  let b: LevelBucket | "all" | undefined = bucket;
  let inferred = false;
  if (!b) {
    const guess = inferLevelFromText(...inferText);
    if (guess) {
      b = guess;
      inferred = true;
    } else {
      b = "all";
    }
  }
  const key = b === "open" ? "open" : b;
  return { bucket: b, label: LABELS[key], color: COLORS[key], inferred };
}
